import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Icon, Statistic, Image, Button, Divider } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import OpportunityManagementEnh from '../../bookWorksheet/components/management/OpportunityManagement-enh';
import { useSharedData } from '../../bookWorksheet/hooks/core/useZoneHooks';
import { fetchStatistics, selectStatisticsByView, selectStatisticsLoading } from '../../slices/statisticsSlice';
import { STAT_SETS } from '../../constants/statisticsFlags';
import {
  fetchOpportunities,
  selectOpportunities,
} from '../../slices/opportunityDataSlice';
import useIsLoggedIn from '../../isLoggedIn';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg'
import hourGlass from '../../assets/hourGlassLine.svg'
import checkBox from '../../assets/checkListLine.svg'
import myLibrary from '../../assets/myLibrary.svg'
import profileIcon from '../../assets/profileLineGlasses.svg'
import myProfileLine from '../../assets/myProfileLine.svg'
import lightening from '../../assets/lightening.svg'

const ManageOpportunitiesLandingPage = () => {
  const dispatch = useDispatch();
  const authToken = useIsLoggedIn();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Book Creator';

  // Get statistics from Redux
  const portfolioStats = useSelector(selectStatisticsByView('opportunity_portfolio'));
  const isLoadingStats = useSelector(selectStatisticsLoading('opportunity_portfolio'));

  // Get shared data for selected book
  const sharedData = useSharedData();
  const { books, selections } = sharedData;
  const selectedBook = books.find(b => b.uuid === selections.bookId) || null;

  // Get opportunities data from Redux
  const allOpportunities = useSelector(selectOpportunities);

  // Filter opportunities by selected book
  const opportunities = useMemo(() => {
    if (!selectedBook || !allOpportunities) return [];

    // Filter opportunities that belong to the selected book
    return allOpportunities.filter(opp => opp.book?.uuid === selectedBook.uuid);
  }, [allOpportunities, selectedBook]);

  // Statistics state
  const [stats, setStats] = useState({
    totalOpportunities: 0,
    openOpportunities: 0,
    assignedOpportunities: 0,
    completedOpportunities: 0
  });

  // Initialize page data
  useEffect(() => {
    if (authToken) {
      // Fetch statistics for opportunity portfolio
      dispatch(fetchStatistics({
        viewType: 'opportunity_portfolio',
        flags: STAT_SETS.OPPORTUNITY_PORTFOLIO
      }));

      // Fetch opportunities
      dispatch(fetchOpportunities());
    }
  }, [dispatch, authToken]);

  // Calculate opportunity statistics from selected book
  useEffect(() => {
    if (opportunities && opportunities.length > 0) {
      const totalOpportunities = opportunities.length;
      const openOpportunities = opportunities.filter(opp => opp.status === 'Open').length;
      const assignedOpportunities = opportunities.filter(opp => opp.status === 'Assigned').length;
      const completedOpportunities = opportunities.filter(opp => opp.status === 'Completed').length;

      setStats({
        totalOpportunities,
        openOpportunities,
        assignedOpportunities,
        completedOpportunities
      });
    } else {
      // Clear stats for books without opportunities - show zeros instead of stale data
      setStats({
        totalOpportunities: 0,
        openOpportunities: 0,
        assignedOpportunities: 0,
        completedOpportunities: 0
      });
    }
  }, [opportunities]);

  // Statistics for the overview section - using new statistics system
  const statistics = [
    {
      label: 'Literary Groups',
      value: isLoadingStats ? '...' : (portfolioStats?.litGroupCount || '0'),
      icon: 'users',
      img: myLibrary,
      link: '/add-literary-group-landing'
    },
    {
      label: 'Total Opportunities',
      value: isLoadingStats ? '...' : (portfolioStats?.opportunityCount || stats.totalOpportunities).toString(),
      icon: 'clipboard list',
      img: checkBox,
      link: '/opportunities/manage'
    },
    {
      label: 'Open Opportunities',
      value: isLoadingStats ? '...' : (portfolioStats?.openOpportunityCount || stats.openOpportunities).toString(),
      icon: 'hourglass half',
      img: hourGlass,
      link: '/opportunities'
    },
    {
      label: 'Active Assignments',
      value: isLoadingStats ? '...' : (portfolioStats?.assignmentCount || stats.assignedOpportunities).toString(),
      icon: 'check circle',
      img: lightening,
      link: '/opportunities/manage'
    },
    {
      label: 'My Profile',
      value: '',
      icon: 'user',
      img: profileIcon,
      link: '/profile'
    }
  ];

  return (
    <Container fluid className="landing-page book-creator-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/shutterstock_2294396055-1024x1024.2d3b550af78ebc4d2e7b.jpg" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Opportunity Management Hub</span>
              <p className="landing-subheading-text">
                Manage voice acting opportunities for your literary works from this centralized hub.
                Create opportunities for your characters, review applications from voice actors,
                assign roles, and track the progress of your voice acting projects.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={myProfileLine} alt="" />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Opportunity Portfolio
                  <Header.Subheader style={{ marginTop: "5px" }}>Overview of your voice acting opportunities</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div>
                <Statistic.Group widths="five">
                  {statistics.slice(0, 5).map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>

                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>

                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          className="statistic-link statistic-link-wide"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
                {/* <Statistic.Group widths="five">
                  {statistics.slice(3, 5).map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          className="statistic-link statistic-link-wide"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group> */}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        <Header as="h2" dividing>
          <img src={lightening} alt="" />
          <Header.Content>
            Opportunity Management
            <Header.Subheader>Create, manage, and track voice acting opportunities</Header.Subheader>
          </Header.Content>
        </Header>

        {/* Main Opportunity Management Component */}
        {/* <div style={{ marginTop: '2em' }}> */}
        <OpportunityManagementEnh />
        {/* </div> */}

        <Grid columns={2} stackable style={{ marginTop: '3em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={myLibrary} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Management Tips
                <Header.Subheader>Best practices for opportunity management</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="lightbulb" color="yellow" />
                  <Header.Content>Pro Tips</Header.Content>
                </Header>
                <p>• Create opportunities for characters with multiple voice variations</p>
                <p>• Set clear deadlines and compensation details</p>
                <p>• Review voice actor applications promptly</p>
                <p>• Provide detailed character descriptions and requirements</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/opportunities/create"
                  compact
                  primary
                >
                  Create New Opportunity
                </Button>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Next Steps
                <Header.Subheader>After creating opportunities</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="arrow right" color="blue" />
                  <Header.Content>What's Next?</Header.Content>
                </Header>
                <p>• Review applications from voice actors</p>
                <p>• Listen to voice samples and select actors</p>
                <p>• Assign roles and create contracts</p>
                <p>• Track progress and manage deliverables</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/opportunities"
                  compact
                  color="blue"
                >
                  Browse Marketplace
                </Button>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default ManageOpportunitiesLandingPage;
